import React from 'react';
import { LibraryAsset, SlideState } from '../types';
import AudioLibraryPicker from '../AudioLibraryPicker';

export interface AudioTabProps {
  slide: SlideState;
  /** Only a clip carries its own audio track to mute or duck. */
  isClip: boolean;
  audioLibrary: LibraryAsset[];
  loadingLibrary: boolean;
  updateActive: (patch: Partial<SlideState>) => void;
  addLibraryAudio: (asset: LibraryAsset, mode: 'foreground' | 'background') => void;
  removeAudio: (audioIdx: number) => void;
  onUploadForeground: () => void;
  onUploadBackground: () => void;
}

const AudioTab: React.FC<AudioTabProps> = ({
  slide,
  isClip,
  audioLibrary,
  loadingLibrary,
  updateActive,
  addLibraryAudio,
  removeAudio,
  onUploadForeground,
  onUploadBackground,
}) => (
  <>
    {isClip && (
      <div className="se-field">
        <label className="se-label">Clip Audio</label>
        <label className="se-checkbox-label">
          <input
            type="checkbox"
            checked={slide.sourceMuted}
            onChange={e => updateActive({ sourceMuted: e.target.checked })}
          />
          <span>Mute the clip's own audio</span>
        </label>
        <div className="se-audio-controls">
          <div className="se-volume-control">
            <label className="se-control-label">Clip volume:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={slide.sourceVolume}
              disabled={slide.sourceMuted}
              onChange={e => updateActive({ sourceVolume: parseInt(e.target.value) })}
              className="se-volume-slider"
            />
            <span className="se-volume-value">{slide.sourceMuted ? 'Muted' : `${slide.sourceVolume}%`}</span>
          </div>
        </div>
        <div className="se-help-text">
          ℹ️ Clip audio is mixed under any Story audio added below
        </div>
      </div>
    )}

    {/* Foreground Audio Effect */}
    <div className="se-field">
      <div className="se-field-header">
        <label className="se-label">Foreground Audio Effect</label>
        <button
          className="se-btn-upload se-btn-upload-sm"
          onClick={onUploadForeground}
          title="Upload your own audio"
        >
          + Upload
        </button>
      </div>

      <AudioLibraryPicker
        assets={audioLibrary}
        loading={loadingLibrary}
        onSelect={asset => addLibraryAudio(asset, 'foreground')}
      />

      {slide.audioFiles.filter(af => af.mode === 'foreground').length > 0 && (
        <div className="se-audio-file-list">
          {slide.audioFiles.map((af, ai) => af.mode === 'foreground' && (
            <div key={af.clientId} className="se-audio-file-item">
              <span className="se-audio-icon">🎵</span>
              <span className="se-audio-filename">{af.fileName}</span>
              <button className="se-audio-delete" onClick={() => removeAudio(ai)}>×</button>
            </div>
          ))}
        </div>
      )}

      <div className="se-audio-controls">
        <div className="se-gap-control">
          <label className="se-control-label">Gap between files:</label>
          <input
            type="number"
            min="0"
            max="10"
            step="0.5"
            value={slide.foregroundGap}
            onChange={e => updateActive({ foregroundGap: parseFloat(e.target.value) || 2.0 })}
            className="se-gap-input"
          />
          <span className="se-unit">seconds</span>
        </div>

        {!slide.longDescriptionText.trim() && slide.audioFiles.filter(af => af.mode === 'foreground').length > 0 && (
          <label className="se-checkbox-label">
            <input
              type="checkbox"
              checked={slide.matchAudioDuration}
              onChange={e => updateActive({ matchAudioDuration: e.target.checked })}
            />
            <span>Match Audio Duration</span>
            <span className="se-hint-text">(Only available when no narration text)</span>
          </label>
        )}
      </div>
    </div>

    {/* Background Audio Effect */}
    <div className="se-field">
      <div className="se-field-header">
        <label className="se-label">Background Audio Effect</label>
        <button
          className="se-btn-upload se-btn-upload-sm"
          onClick={onUploadBackground}
          title="Upload your own audio"
        >
          + Upload
        </button>
      </div>

      <AudioLibraryPicker
        assets={audioLibrary}
        loading={loadingLibrary}
        onSelect={asset => addLibraryAudio(asset, 'background')}
      />

      {slide.audioFiles.filter(af => af.mode === 'background').length > 0 && (
        <div className="se-audio-file-list">
          {slide.audioFiles.map((af, ai) => af.mode === 'background' && (
            <div key={af.clientId} className="se-audio-file-item">
              <span className="se-audio-icon">🎵</span>
              <span className="se-audio-filename">{af.fileName}</span>
              <button className="se-audio-delete" onClick={() => removeAudio(ai)}>×</button>
            </div>
          ))}
        </div>
      )}

      <div className="se-audio-controls">
        <div className="se-volume-control">
          <label className="se-control-label">Volume:</label>
          <input
            type="range"
            min="0"
            max="100"
            value={slide.backgroundVolume}
            onChange={e => updateActive({ backgroundVolume: parseInt(e.target.value) })}
            className="se-volume-slider"
          />
          <span className="se-volume-value">{slide.backgroundVolume}%</span>
        </div>

        {slide.audioFiles.filter(af => af.mode === 'background').length > 0 && (
          <div className="se-info-banner">
            ℹ️ Background audio will loop to match slide duration
          </div>
        )}
      </div>
    </div>
  </>
);

export default AudioTab;
